import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {enumerate} from 'ts-transformer-enumerate';
import {v4 as uuidV4} from 'uuid';
import _ from 'lodash';
import memoize from 'memoize-one';

import {Button} from 'primereact/button';
import {ContextMenu} from 'primereact/contextmenu';
import {Dialog} from 'primereact/dialog';
import {Toast} from 'primereact/toast';
import TreeNode from 'primereact/treenode/TreeNode';

import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';
import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import metaModel from '~/shared/src/utilities/metaModel';
import Item from '~/shared/src/ci/Item';
import {ReferenceAttributesForCreation} from '~/shared/src/ci/Reference';
import {extractDataObjectsFromDataStructure} from '~/shared/src/utilities/dataStructure';
import dataUtils from '~/shared/src/utilities/dataUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {
    rawModeSchema,
    showAdditionalRawNamesSchema
} from '~/shared/src/validationSchemes/modules/overview';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import {
    CheckItemKeyUniquenessParameters,
    CheckItemKeyUniquenessResult,
    SaveDataResult
} from '~/shared/src/utilities/modules/data';
import {
    ClearConfigParameters,
    GetFormItemIdByDisplayAttributeResult
} from '~/shared/src/utilities/modules/form';

import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import Headerbar from '~/frontend/src/components/base/FormDesigner/Headerbar';
import DesignerTree, {
    ReferenceType as DesignerTreeReferenceType
} from '~/frontend/src/components/base/FormDesigner/DesignerTree';
import Toolbar from '~/frontend/src/components/base/FormDesigner/Toolbar';
import ResizeableLeftMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableLeftMenuPanel';
import ResizeableRightMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableRightMenuPanel';
import Form from '~/frontend/src/components/base/Form';
import FormDesigner, {
    DragData,
    DragOptions,
    Mode as FormDesignerMode,
    ReferenceType as FormDesignerReferenceType
} from '~/frontend/src/components/base/FormDesigner';
import AttributesForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';
import FieldWrapper from '~/frontend/src/components/base/shared/field/FieldWrapper';
import ItemTypeDropdown from '~/frontend/src/components/base/shared/field/ItemTypeDropdown';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';
import SchemaDropdown from '~/frontend/src/components/base/shared/field/SchemaDropdown';

import {
    isDataModified,
    isDataModifiedRecursive,
    MergeMode
} from '~/frontend/src/utilities/dataUtils';
import {
    getPanelIcon as getPanelIconOrig,
    getPanelTitle as getPanelTitleOrig,
    ItemTitleConfig,
    setPanelInfo as setPanelInfoOrig
} from '~/frontend/src/utilities/panelInfo';
import {
    getPanelElusiveData,
    getPanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {FormPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/definitions';

import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {
    ElusiveDataState,
    PanelElusiveDataState
} from '~/frontend/src/reducers/elusiveData';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import loadData from '~/frontend/src/actions/data/loadData';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import * as TreeActions from '~/frontend/src/actions/panel/treeView';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {
    MsgParams,
    setMessage,
    unsetMessage
} from '~/frontend/src/actions/intercom';
import {
    clearElusiveData,
    clearElusiveDataForSubpanels,
    setElusiveData
} from '~/frontend/src/actions/elusiveData';
import * as CiActions from '~/frontend/src/actions/data';
import setParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import setPanelDirty, {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import setPanelInfo, {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import saveData, {SaveData} from '~/frontend/src/actions/panel/viewDesigner/saveData';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import addItem, {AddItem} from '~/frontend/src/actions/data/addItem';
import addReference, {AddReference} from '~/frontend/src/actions/data/addReference';
import {DeleteReference} from '~/frontend/src/actions/data/deleteReference';
import cancelRequests from '~/frontend/src/actions/ajax/cancelRequests';


type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    showCouldNotSavePopup? : boolean
};

type Variables = {
    [ variable : string ] : any;
};


const defaultProps = {
    /**
     * The database schema to filter data and metadata for.
     */
    dataContext: 'formDesigner' as string,
    //
    title: 'FormDesignerView' as string,
    //
    schema: 'DATA' as Schema,

    /**
     * The width of the menu on the left side in percent.
     */
    menuWidth: 15 as number,

    /**
     * The width of the menu on the right side in percent.
     */
    menu2Width: 20 as number,

    /**
     * The status to filter data for.
     */
    status: 'active' as Status,

    /**
     * The timestamp to filter data at.
     */
    timestamp: 'now' as string,

    /**
     * The timezone to filter data at.
     */
    timezone: 'Europe/Berlin' as string,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    mode: 'design' as FormDesignerMode,

    showHiddenLayoutElements: false as boolean,

    newLayoutElementsWidth: '100%' as string
};
type DefaultProps = Readonly<typeof defaultProps>;

/**
 * Definition of components props
 * @memberof TreeTest
 * @property {String} dataContext='cypher' The ci-data context of this component
 * @property {Object} items CI-Data as stored in redux store
 * @property {String} itemsVersion Version of CI-Data as stored in redux store
 */
type Props = {
    uid : string;

    /**
     * The ItemId to filter data and metadata for.
     */
    itemId? : number;

    // the ciId of the existing form
    rootItemId : number

    // the key of the new or existing form
    rootItemKey : string;

    // the name of the new or existing form
    rootItemName : string;

    // the id of the root item to display data in the form
    dataRootItemId? : number;

    payloadItemType? : string;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode? : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames? : boolean;

    selectedLayoutElementId? : number;

    referenceToCreateForNew? : ReferenceAttributesForCreation;

    /**
     * The props for sub panels
     */
    subPanelProps? : any;

    onSave? : ( rootItemId : number, dataContext : string ) => void;

    /**
     * The full elusiveData
     */
    allElusiveData? : ElusiveDataState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     * A storage for temporary needed data (not persisted to local storage via redux store persist), like
     *  - lastParams: The stringified parameters of the last loaded search. Used to determine if a new result have to be loaded from the backend.
     *  - gridData: The ordered itemIds to display in the grid.
     *  - totalRecords: The total result data count to be displayed in the DataTable.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;
};

const Actions = Object.assign( { loadData, setMessage, unsetMessage, setElusiveData, clearElusiveData, clearElusiveDataForSubpanels, setPanelInfo, setPanelDirty, addItem, addReference, closePanel }, PanelActions, TreeActions, IntercomActions, CiActions );

type SetParam = <K extends keyof ComponentProps>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {
    //updatePanelProps: typeof updatePanelProps;
    loadData : typeof loadData;
    setParam : SetParam;
    setParams : SetParams;
    unsetParam : UnsetParam;
    setMessage : typeof setMessage;
    unsetMessage : typeof unsetMessage;
    setPanelInfo : SetPanelInfo;
    setElusiveData : typeof setElusiveData;
    clearElusiveData : typeof clearElusiveData;
    clearElusiveDataForSubpanels : typeof clearElusiveDataForSubpanels;
    setPanelDirty : SetPanelDirty;
    deleteReference : DeleteReference;
    addItem : AddItem;
    addReference : AddReference;

};

type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
} & {
    closePanel : ClosePanel;
};

type AllProps = Props & DefaultProps & ActionProps;
export type ComponentProps = Partial<DefaultProps> & Props;

type State = {
    // the ciId of the currently selected tree node
    rootItemId : number;
    selectedItemParentId? : number;

    isDirty : boolean;
    errorCount : number;
    errorTooltip? : string | React.ReactNode | ( string | React.ReactNode )[];
    errorMessage? : string | React.ReactNode | ( string | React.ReactNode )[];
    rootItemFormState : 'CLEAR' | 'DIRTY' | 'ERROR';

    /**
     * Counts up on menu scroll. Used to fire update for arrows
     */
    scrollIndicator : number;

    initialized : boolean;

    formItemTypeId : number;

    showCreateNewFormDialog : boolean;

    createNewFormDialogKey? : string;
    createNewFormDialogKeyError? : string;
    createNewFormDialogSchema : Schema;
    createNewFormDialogItemType? : string;

    mobile : boolean;
};


const schemata  = enumerate<YnSchema>(),
      getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches ? true : false;


/**
 * Playground to test Tree implementation
 */
class FormDesignerView extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static panelType = 'FormDesignerView';
    static panelTabTitle = 'YN.base.frontend.formDesigner.tabTitle';
    static panelTabIcon = 'itemTypeIcon:CFG:Form';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.frontend.formDesigner';
    static panelPropsSchema = Joi.object()
        // panelSchema defaults
        .concat( requestBaseParametersSchema )
        .concat( Joi.object( {
            panelType      : Joi.string().valid( FormDesignerView.panelType ),
            rootItemType   : Joi.string(),
            panelTabTitle  : Joi.string().default( FormDesignerView.panelTabTitle ),
            panelTabIcon   : Joi.string().default( FormDesignerView.panelTabIcon ),
            rootItemId     : Joi.number().description( 'Id of the form.' ),
            rootItemKey    : Joi.string().description( 'Key of form' ),
            payloadItemType: Joi.string().description( 'Item Type of the payload root item (only needed for new forms)' ),
            menuWidth      : Joi.number()
                .default( 15 )
                .description( 'The width of the left menu panel in percent of the available width.' ),
            menu2Width     : Joi.number()
                .default( 30 )
                .description( 'The width of the right menu panel in percent of the available width.' ),
            // this is only for compatibility with ItemFinder overview (if we open this panel from there)
            rawMode                : rawModeSchema,
            showAdditionalRawNames : showAdditionalRawNamesSchema,
            title                  : Joi.string(),
            referenceToCreateForNew: Joi.object( {
                srcItemId     : Joi.number()
                    .required(),
                destItemId    : Joi.number()
                    .required(),
                referenceSetId: Joi.number()
                    .required(),
                statusId      : Joi.number()
                    .required()
            } ),
            onSave                 : Joi.function()
        } ) );

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

            // abort running request
            dispatch( cancelRequests( props.uid ) );

            // clear global data
            if ( props.dataContext ) {
                delete global.data[ props.dataContext ];
                delete global.data[ `${props.dataContext}-meta` ];
                delete global.data[ `${props.dataContext}_data` ];
                delete global.data[ `${props.dataContext}_DataObjects` ];
            }

            // clear backend data of all loaded forms
            for ( const subPanelProps of Object.values( props.subPanelProps ?? {} ) as any[] ) {
                if ( subPanelProps.moduleTransformer === 'form' && subPanelProps.configHash ) {
                    const params : ClearConfigParameters = {
                        configHash: subPanelProps.configHash
                    };

                    await xhr.post( `${BASE_URL}/api/form/clearConfig`, params, undefined, props.uid, props.uid, dispatch );
                }
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await FormDesignerView.savePanelData(
                dispatch,
                getState,
                props
            );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await FormDesignerView.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : Pick<AllProps, 'dataContext' | 'rootItemId' | 'rootItemKey'> ) : string | undefined {
        const {
                  dataContext,
                  rootItemId,
                  rootItemKey
              }      = props,
              itemId = rootItemId
                  ?? dataUtils.findItem( dataContext, 'Form', rootItemKey )?.id
                  ?? 0,
              item   = global.data[ dataContext ]?.items?.[ itemId ];

        let retVal : string | undefined;

        if ( item ) {
            retVal = `#${FormDesignerView.panelType}/${encodeURIComponent( item.displayAttributeValue )}`;
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = rootItem.displayAttributeValue
        if ( params?.[ 1 ] ) {
            const displayAttributeValue = decodeURIComponent( params[ 1 ] );

            // resolve viewKey and rootItemId
            const res    = await xhr.post(
                      `${BASE_URL}/api/form/getFormItemIdByDisplayAttribute`,
                      {
                          displayAttributeValue
                      }
                  ),
                  result = res?.data as GetFormItemIdByDisplayAttributeResult;

            if ( result ) {
                if ( result.data.rootItemId ) {
                    retVal.rootItemId = result.data.rootItemId;
                }
                else {
                    stackError.add( new LocalizableError( 'YN.base.designer.resolvePanelPropsFromURL.error.couldNotResolveRootItemId', { attribute: displayAttributeValue } ) );
                }
            }
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.designer.resolvePanelPropsFromURL.error.couldNotResolveRootItem' ) );
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        return retVal;
    }

    static async doSaveData( uid : string, dispatch : AppDispatch, successMsgParams : MsgParams | undefined = undefined ) : ReturnType<ReturnType<SaveData>> {
        const props                                              = getPanelProps( uid )?.panelProps as ComponentProps,
              elusiveData                                        = getPanelElusiveData( uid ),
              propsWithDefaults : Required<DefaultProps> & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  rootItemId,
                  selectedLayoutElementId,
                  onSave,
                  subPanelProps
              }                                                  = propsWithDefaults,
              rootItemIdForSave                                  = rootItemId ?? elusiveData?.data?.rootItemId,
              result                                             = await dispatch( saveData( uid, 'CFG', rootItemIdForSave, undefined, undefined, undefined, successMsgParams ) );

        // remap selectedLayoutElement id
        if ( selectedLayoutElementId && result && result.data.itemMetaDataMapping[ String( selectedLayoutElementId ) as unknown as number ] ) {
            dispatch( setParam( uid, 'selectedLayoutElementId', result.data.itemMetaDataMapping[ String( selectedLayoutElementId ) as unknown as number ].id ) );
        }

        // remap activeTabContainerIndexes
        if ( result && result.data.referenceMetaDataMapping && Object.keys( result.data.referenceMetaDataMapping ).length ) {
            for ( const subPanelKey of Object.keys( subPanelProps ) as string[] ) {
                if ( subPanelProps[ subPanelKey ].activeTabContainerIndexes ) {
                    const activeTabContainerIndexes    = subPanelProps[ subPanelKey ].activeTabContainerIndexes,
                          newActiveTabContainerIndexes = { ...activeTabContainerIndexes };

                    let remapped : boolean = false;

                    for ( const realTabContainerLayoutElementId of Object.keys( newActiveTabContainerIndexes ) ) {
                        if ( newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] < 0 && result.data.referenceMetaDataMapping[ newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] ] ) {
                            remapped = true;
                            newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] = result.data.referenceMetaDataMapping[ newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] ].id;
                        }
                    }

                    if ( remapped ) {
                        dispatch( setParam(
                            `${uid}_${subPanelKey}`,
                            'activeTabContainerIndexes',
                            newActiveTabContainerIndexes
                        ) );
                    }
                }
            }
        }

        if ( onSave ) {
            // remap rootItem id
            if ( result && result.data.itemMetaDataMapping[ String( rootItemIdForSave ) as unknown as number ] ) {
                onSave( result.data.itemMetaDataMapping[ String( rootItemIdForSave ) as unknown as number ].id, uid );
            }
            else {
                onSave( rootItemIdForSave, uid );
            }
        }

        return result;
    }

    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid
              }                                        = propsWithDefaults,
              allElusiveData                           = getState()?.elusiveData;

        let retVal : Promise<SaveDataResult | false> = Promise.resolve( false ),
            hasError : boolean                       = false;

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData;

                    if ( panelElusiveData.errorData?.count ) {
                        hasError = true;
                        break;
                    }
                }
            }
        }

        // panel has errors -> show popup
        if ( hasError ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );
        }

        // panel has no errors -> save
        else {
            retVal = FormDesignerView.doSaveData( uid, dispatch, successMsgParams );
        }

        return retVal;
    }

    menuContentPanelContentStyle = { padding: 0 };
    menuContentPanelsPt = {
        gutter: {
            style: {
                display: 'none'
            }
        }
    };

    isLoading : boolean = false;

    cm : InstanceType<typeof ContextMenu> | null = null;
    toast : Toast | null = null;
    tree : DesignerTreeReferenceType | null = null;
    formDesigner : FormDesignerReferenceType | null = null;

    getTreeContextMenu = ( treeData : TreeNode.TreeNode[], key : string ) => [
        {
            label  : 'Collapse all',
            icon   : 'far fa-minus-square',
            command: function() {
                this.setState( { [ key ]: {} } );
            }.bind( this )
        },
        {
            label  : 'Expand all',
            icon   : 'far fa-plus-square',
            command: function() {
                const expandedKeys : {
                    [ key : string ] : true
                } = {};
                const expandNode = ( node : TreeNode.TreeNode ) => {
                    if ( node.children?.length ) {
                        expandedKeys[ node.key! ] = true;

                        for ( const child of node.children ) {
                            expandNode( child );
                        }
                    }
                }

                for ( const node of treeData ) {
                    expandNode( node );
                }

                this.setState( { [ key ]: expandedKeys } );
            }.bind( this )
        }
    ];

    getPanelIcon = getPanelIconOrig;
    getPanelTitle = getPanelTitleOrig;
    setPanelInfo = setPanelInfoOrig;

    /**
     * React Component constructor
     * @param {Object} props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            rootItemId: 0 as number,

            isDirty          : false,
            errorCount       : 0,
            rootItemFormState: 'CLEAR',

            scrollIndicator: 0 as number,

            initialized: false,

            formItemTypeId: metaModel[ 'CFG' ].getItemType( 'Form' )?.id ?? 0,

            showCreateNewFormDialog: false,

            createNewFormDialogKey     : props.rootItemKey ? props.rootItemKey : undefined,
            createNewFormDialogSchema  : 'DATA',
            createNewFormDialogItemType: props.payloadItemType ? props.payloadItemType : undefined,

            mobile: getMobile()
        };

        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.getDesignerTree = memoize( this.getDesignerTree, this.getDesignerTreeEqual );
        this.getForm = memoize( this.getForm, this.getFormEqual );
        this.getFormDesigner = memoize( this.getFormDesigner, this.getFormDesignerEqual );
        this.getHeaderbar = memoize( this.getHeaderbar );
        this.getPanelIcon = memoize( this.getPanelIcon );
        this.getPanelTitle = memoize( this.getPanelTitle );
        this.getToolbar = memoize( this.getToolbar );
        this.setPanelInfo = memoize( this.setPanelInfo );
        this.getSelectableSchemata = memoize( this.getSelectableSchemata );

        this.checkKeyUniqueness = _.throttle( this.checkKeyUniqueness, 200, { trailing: true } );
        this.onMenuScroll = _.throttle( this.onMenuScroll, 100, { trailing: true } );

        this.checkForCreateNewFormPrerequisits = this.checkForCreateNewFormPrerequisits.bind( this );
        this.onCreateNewFormDialogHide = this.onCreateNewFormDialogHide.bind( this );
        this.onCreateNewFormDialogOkButtonClick = this.onCreateNewFormDialogOkButtonClick.bind( this );
        this.onCreateNewFormDialogSetItemType = this.onCreateNewFormDialogSetItemType.bind( this );
        this.onCreateNewFormDialogSetKey = this.onCreateNewFormDialogSetKey.bind( this );
        this.onCreateNewFormDialogSetSchema = this.onCreateNewFormDialogSetSchema.bind( this );
        this.onDataRootItemIdChange = this.onDataRootItemIdChange.bind( this );
        this.onMarkFieldsForAlias = this.onMarkFieldsForAlias.bind( this );
        this.onMarkLayoutElementsForAlias = this.onMarkLayoutElementsForAlias.bind( this );
        this.onMatchMobileChangeHandler = this.onMatchMobileChangeHandler.bind( this );
        this.onMenu2WidthChange = this.onMenu2WidthChange.bind( this );
        this.onMenuWidthChange = this.onMenuWidthChange.bind( this );
        this.onNewLayoutElementDragStart = this.onNewLayoutElementDragStart.bind( this );
        this.onNewLayoutElementsWidthChange = this.onNewLayoutElementsWidthChange.bind( this );
        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
        this.onSelectLayoutElement = this.onSelectLayoutElement.bind( this );
        this.onShowHiddenLayoutElementsChange = this.onShowHiddenLayoutElementsChange.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  uid,
                  rootItemId,
                  rootItemKey,
                  dataContext,
                  subPanelProps,
                  allElusiveData
              }                                              = props,
              tooltipErrors : ( string | React.ReactNode )[] = [],
              messageErrors : ( string | React.ReactNode )[] = [];

        let isDirty : boolean                              = rootItemId ? isDataModifiedRecursive( dataContext, 'CFG', rootItemId ) : isDataModified( dataContext, 'CFG' ),
            errorCount                                     = 0,
            rootItemFormState : State['rootItemFormState'] = 'CLEAR';

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData,
                          panelKey         = elusiveDataPanelKey.replace( `${uid}_`, '' );

                    // only check direct sub forms
                    if ( panelKey.match( /^form-/ ) && panelKey.indexOf( '_' ) === -1 ) {
                        const panelProps   = subPanelProps?.[ panelKey ],
                              formRootItem = panelProps
                                  ? global.data[ dataContext ]?.items[ panelProps.rootItemId ]
                                  : undefined,
                              elementName  = formRootItem
                                  ? formRootItem.name
                                      ? formRootItem.name
                                      : formRootItem?.key
                                  : '',
                              elementType  = formRootItem ? formRootItem.itemType.label : 'Element';

                        isDirty = isDirty || !!panelElusiveData.isDirty;

                        if ( formRootItem?.id === rootItemId ) {
                            if ( panelElusiveData.errorData?.count ) {
                                rootItemFormState = 'ERROR';
                            }
                            else if ( panelElusiveData.isDirty ) {
                                rootItemFormState = 'DIRTY';
                            }
                        }

                        if ( panelElusiveData.errorData?.count ) {
                            errorCount += panelElusiveData.errorData.count;

                            if ( panelElusiveData.errorData.tooltip?.length ) {
                                tooltipErrors.push(
                                    <>
                                        <div className="yn-tooltip-header2">
                                            {__( 'YN.base.frontend.formDesigner.error.tooltip.title', { type: elementType, name: elementName } )}
                                        </div>
                                        {panelElusiveData.errorData.tooltip}
                                    </>
                                );

                                if ( panelElusiveData.errorData.message?.length ) {
                                    messageErrors.push(
                                        <>
                                            <div className="yn-error-subtitle">
                                                {__( 'YN.base.frontend.formDesigner.error.message.title', { type: elementType, name: elementName } )}
                                            </div>
                                            <hr className="yn-error-ruler"/>
                                            <div className="yn-error-suberror">
                                                {panelElusiveData.errorData.message}
                                            </div>
                                        </>
                                    );
                                }
                            }
                        }
                    }
                }
            }
        }

        return {
            rootItemId  : rootItemId || ( dataContext && rootItemKey ? dataUtils.findItem( dataContext, 'Form', rootItemKey )?.id ?? 0 : state.rootItemId ),
            isDirty,
            errorCount,
            errorTooltip: errorCount
                ? <div key="error" className="yn-tooltip-error">
                    <div className="yn-tooltip-header">
                        {__( 'YN.base.frontend.dataTable.tooltip.error.title' )}
                    </div>
                    {tooltipErrors}
                </div>
                : undefined,
            errorMessage: errorCount
                ? <>
                    <div className="yn-error-message">
                        {__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.text' )}
                    </div>
                    <hr className="yn-error-ruler"/>
                    <div className="yn-error-suberror">
                        {messageErrors}
                    </div>
                </>
                : undefined,
            rootItemFormState
        };
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        matchMedia( 'screen and (max-width: 960px)' ).addEventListener( 'change', this.onMatchMobileChangeHandler );

        this.afterRender();

        if ( !this.props.elusiveData ) {
            if ( this.props.rootItemId || this.props.rootItemKey ) {
                void this.loadData();
            }
            else {
                this.checkForCreateNewFormPrerequisits();
            }
        }
        else {
            this.setState( {
                initialized: true
            } );
        }
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        matchMedia( 'screen and (max-width: 960px)' ).removeEventListener( 'change', this.onMatchMobileChangeHandler );
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : Readonly<AllProps>, prevState : Readonly<State> ) {
        this.afterRender();

        // key changed or dialog shown
        if (
            prevState.createNewFormDialogKey !== this.state.createNewFormDialogKey
            || ( !prevState.showCreateNewFormDialog && this.state.showCreateNewFormDialog )
        ) {
            this.setState( { createNewFormDialogKeyError: __( 'YN.base.finder.item.key.check.unique' ) } );
            this.checkKeyUniqueness();
        }
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        if (
            ( nextState.isDirty !== undefined && nextState.isDirty !== this.state.isDirty )
        ) {
            this.props.setPanelDirty( this.props.uid, nextState.isDirty );
        }

        return !this.isLoading;
    }

    async loadData() : Promise<boolean> {
        this.isLoading = true;

        const {
                  dataContext,
                  rootItemId,
                  rootItemKey,
                  uid
              }      = this.props,
              retVal = await this.props.loadData?.(
                  'form/loadConfigData',
                  dataContext,
                  dataContext,
                  undefined,
                  'CFG',
                  {
                      params   : rootItemId ? {
                          viewId: rootItemId
                      } : {
                          viewKey: rootItemKey
                      },
                      mergeMode: 'keepExisting' as MergeMode
                  },
                  undefined,
                  resonseData => {
                      if ( resonseData?.data.rootItemId && rootItemId !== resonseData.data.rootItemId ) {
                          this.props.setParam( uid, 'rootItemId', resonseData.data.rootItemId );
                      }

                      if ( !resonseData?.data.items.length ) {
                          this.checkForCreateNewFormPrerequisits();
                      }
                      else {
                          this.setState( {
                              initialized: true
                          } );
                      }
                  }
              );

        this.isLoading = false;

        return retVal;
    }

    afterRender( props : AllProps = this.props as AllProps ) {
        const {
                  uid,
                  dataContext,
                  rootItemId,
                  rootItemKey,
                  intercomMessages
              }                                             = props,
              {
                  isDirty,
                  errorCount
              }                                             = this.state,
              icon                                          = this.getPanelIcon( FormDesignerView.panelTabIcon ),
              itemTitleConfig : ItemTitleConfig | undefined = rootItemId
                  ? {
                      dataContext,
                      id: rootItemId
                  }
                  : undefined,
              title                                         = this.getPanelTitle( FormDesignerView.panelTabTitle, undefined, itemTitleConfig ),
              path                                          = FormDesignerView.buildPanelPathFromProps( { dataContext, rootItemId, rootItemKey } );

        this.checkForIntercomMessages( intercomMessages );

        this.setPanelInfo( uid, icon, title, undefined, isDirty, !!errorCount, path );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage?.( this.props.uid, 'toast', key as string );
            }
        }
    }

    checkForCreateNewFormPrerequisits() {
        const {
                  createNewFormDialogItemType
              } = this.state;

        // no root DataObject ItemType -> show dialog
        if ( !createNewFormDialogItemType ) {
            this.setState( {
                initialized            : true,
                showCreateNewFormDialog: true
            } );
        }
        else {
            this.setState( {
                initialized: true
            } );

            this.createNewForm();
        }
    }

    /**
     * Checks the given key for uniqueness within the given ItemType against the backend.
     *
     * @private
     */
    checkKeyUniqueness() {
        const {
                  uid
              }          = this.props,
              {
                  formItemTypeId,
                  createNewFormDialogKey
              }          = this.state,
              requestUid = 'checkKeyUniqueness';

        if ( formItemTypeId && createNewFormDialogKey ) {

            // abort running check
            if ( this.props.xhrRequestsForComponent?.[ requestUid ] ) {
                xhr.abort( uid, requestUid );
            }

            const doLoad = async () => {
                const params : CheckItemKeyUniquenessParameters = {
                          schema  : 'CFG',
                          status  : 'all',
                          itemType: formItemTypeId,
                          key     : createNewFormDialogKey
                      },
                      response                                  = await xhr.post(
                          `${BASE_URL}/api/data/checkItemKeyUniqueness`,
                          params,
                          undefined,
                          uid,
                          requestUid
                      );

                if ( response?.data ) {
                    const responseData = response.data as CheckItemKeyUniquenessResult;

                    if ( responseData.success && !responseData.data.keyIsUnique ) {
                        this.setState( { createNewFormDialogKeyError: __( 'YN.base.frontend.formDesigner.createNewDialog.key.error.notUnique' ) } );
                    }
                    else {
                        this.setState( { createNewFormDialogKeyError: '' } );
                    }
                }
            };

            doLoad().catch( ( error ) => {
                console.log( error );
            } );
        }
    }

    createNewForm() {
        const {
                  uid,
                  dataContext,
                  referenceToCreateForNew,
                  rootItemName
              }              = this.props,
              {
                  formItemTypeId,
                  createNewFormDialogKey,
                  createNewFormDialogSchema,
                  createNewFormDialogItemType
              }              = this.state,
              activeStatusId = metaModel[ 'CFG' ].getStatus( 'active' )!.id;

        if ( createNewFormDialogKey && createNewFormDialogItemType ) {
            const form           = this.props.addItem( dataContext, {
                      itemTypeId: formItemTypeId,
                      key       : createNewFormDialogKey,
                      name      : rootItemName ?? createNewFormDialogItemType,
                      statusId  : activeStatusId
                  }, 'CFG' ),
                  rootDataObject = this.props.addItem( dataContext, {
                      itemTypeId: metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                      key       : `${createNewFormDialogKey}:${createNewFormDialogItemType}:${uuidV4().substring( 0, 8 )}`,
                      name      : createNewFormDialogItemType,
                      statusId  : activeStatusId,
                      properties: [
                          {
                              propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DataObject', 'ObjectSchema' )!.id,
                              value        : createNewFormDialogSchema.toLowerCase(),
                              statusId     : activeStatusId
                          },
                          {
                              propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DataObject', 'ObjectType' )!.id,
                              value        : createNewFormDialogItemType,
                              statusId     : activeStatusId
                          }
                      ]
                  }, 'CFG' ),
                  container      = this.props.addItem( dataContext, {
                      itemTypeId: metaModel[ 'CFG' ].getItemType( 'Container' )!.id,
                      key       : `${createNewFormDialogKey}:${uuidV4().substring( 0, 8 )}`,
                      name      : 'Container',
                      statusId  : activeStatusId
                  }, 'CFG' ),
                  stringField    = this.props.addItem( dataContext, {
                      itemTypeId: metaModel[ 'CFG' ].getItemType( 'StringField' )!.id,
                      key       : `${createNewFormDialogKey}:${createNewFormDialogItemType}:name:${uuidV4().substring( 0, 8 )}`,
                      name      : `${createNewFormDialogItemType} name`,
                      statusId  : activeStatusId,
                      properties: [
                          {
                              propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'StringField', 'AttributePath' )!.id,
                              value        : 'item.name',
                              statusId     : activeStatusId
                          },
                          {
                              propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'StringField', 'Order' )!.id,
                              value        : 10,
                              statusId     : activeStatusId
                          },
                          {
                              propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'StringField', 'Width' )!.id,
                              value        : 100,
                              statusId     : activeStatusId
                          }
                      ]
                  }, 'CFG' );

            if ( form && rootDataObject && container ) {
                this.props.addReference( dataContext, {
                    srcItemId     : rootDataObject.id,
                    destItemId    : form.id,
                    referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'Form', 'isRootOf', 'src' )![ 0 ].id,
                    statusId      : activeStatusId
                }, 'CFG' );
                this.props.addReference( dataContext, {
                    srcItemId     : container.id,
                    destItemId    : form.id,
                    referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Container', 'Form', 'belongsTo', 'src' )![ 0 ].id,
                    statusId      : activeStatusId
                }, 'CFG' );
                this.props.addReference( dataContext, {
                    srcItemId     : stringField.id,
                    destItemId    : container.id,
                    referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'StringField', 'Container', 'belongsTo', 'src' )![ 0 ].id,
                    statusId      : activeStatusId
                }, 'CFG' );
                this.props.addReference( dataContext, {
                    srcItemId     : rootDataObject.id,
                    destItemId    : stringField.id,
                    referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'StringField', 'providesDataFor', 'src' )![ 0 ].id,
                    statusId      : activeStatusId
                }, 'CFG' );

                // add additionalReference (replace rootItemId by form.id)
                if ( referenceToCreateForNew ) {
                    if ( referenceToCreateForNew.srcItemId < 0 ) {
                        referenceToCreateForNew.srcItemId = form.id;
                    }
                    else {
                        referenceToCreateForNew.destItemId = form.id;
                    }

                    this.props.addReference( dataContext, referenceToCreateForNew, 'CFG' );
                    this.props.setParam( uid, 'referenceToCreateForNew', undefined );
                }

                this.props.setParam( uid, 'rootItemId', form.id );
            }
        }
    }

    onReloadButtonClick() {
        const {
                  uid,
                  dataContext
              } = this.props;

        if ( dataContext ) {
            this.isLoading = true;

            this.props.clearElusiveData?.( uid );
            this.props.clearElusiveDataForSubpanels?.( uid );

            // clean data
            global.data[ dataContext ] = {
                items     : [],
                references: [],
                schema    : global.data[ dataContext ].schema
            };

            window.setTimeout(
                () => {
                    void this.loadData();
                },
                1
            );
        }
    }

    onNewLayoutElementsWidthChange( newLayoutElementsWidth : string ) {
        this.props.setParam( this.props.uid, 'newLayoutElementsWidth', newLayoutElementsWidth );
    }

    onShowHiddenLayoutElementsChange( showHiddenLayoutElements : boolean ) {
        this.props.setParam( this.props.uid, 'showHiddenLayoutElements', showHiddenLayoutElements );
    }

    onMenuScroll() {
        this.setState( { scrollIndicator: this.state.scrollIndicator! + 1 } )
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenuWidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menuWidth', width );
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenu2WidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menu2Width', width );
    }

    onSelectLayoutElement( selectedLayoutElementId? : number ) {
        this.props.setParam?.( this.props.uid, 'selectedLayoutElementId', selectedLayoutElementId );
    }

    onDataRootItemIdChange( id : number ) {
        this.formDesigner?.clearRootItemData();
        this.props.setParam( this.props.uid, 'dataRootItemId', id );
    }

    onCreateNewFormDialogOkButtonClick() {
        this.createNewForm();
        this.setState( { showCreateNewFormDialog: false } );
    }

    onCreateNewFormDialogHide() {
        this.props.closePanel( this.props.uid );
    }

    onCreateNewFormDialogSetKey( value : string | undefined ) {
        let key      = '',
            keyError = '';

        if ( value !== undefined ) {
            key = value;

            if ( !key ) {
                keyError = __( 'YN.base.finder.item.key.error.empty' );
            }
            else if ( / /.test( key ) ) {
                keyError = __( 'YN.base.finder.item.key.error.whitespaces' );
            }

            this.setState( { createNewFormDialogKey: key, createNewFormDialogKeyError: keyError } );
        }
    }

    onCreateNewFormDialogSetSchema( value : string ) {
        const {
                  createNewFormDialogItemType,
                  createNewFormDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewFormDialogSchema === 'CFG'
                  ? `CFG:${createNewFormDialogItemType ?? ''}`
                  : createNewFormDialogItemType ?? '';

        let {
                createNewFormDialogKey,
                createNewFormDialogKeyError
            } = this.state;

        if ( !createNewFormDialogKey || createNewFormDialogKey === oldGeneratedKey ) {
            createNewFormDialogKey = value === 'CFG' ? 'CFG:' : '';
            createNewFormDialogKeyError = undefined;
        }

        this.setState( {
            createNewFormDialogSchema  : value as Schema,
            createNewFormDialogItemType: undefined,
            createNewFormDialogKey,
            createNewFormDialogKeyError
        } );
    }

    onCreateNewFormDialogSetItemType( value : string ) {
        const {
                  createNewFormDialogItemType,
                  createNewFormDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewFormDialogSchema === 'CFG'
                  ? `CFG:${createNewFormDialogItemType ?? ''}`
                  : createNewFormDialogItemType ?? '';

        let {
                createNewFormDialogKey,
                createNewFormDialogKeyError
            } = this.state;

        if ( !createNewFormDialogKey || createNewFormDialogKey === oldGeneratedKey ) {
            createNewFormDialogKey = createNewFormDialogSchema === 'CFG' ? 'CFG:' + value : value;
            createNewFormDialogKeyError = undefined;
        }

        this.setState( {
            createNewFormDialogItemType: value,
            createNewFormDialogKey,
            createNewFormDialogKeyError
        } );
    }

    onNewLayoutElementDragStart( rootLayoutElementId : number, data : DragData, options : DragOptions, e : React.DragEvent<HTMLElement> ) : void {
        this.formDesigner?.startDrag( rootLayoutElementId, data, options, e );
    }

    onMarkLayoutElementsForAlias( alias : ( string | undefined ) ) : void {
        this.formDesigner?.markLayoutElementsForAlias( alias );
    }

    onMarkFieldsForAlias( alias : ( string | undefined ) ) : void {
        this.tree?.markFieldsForAlias( alias );
    }

    onMatchMobileChangeHandler() {
        this.setState( { mobile: getMobile() } );
    }

    getSelectableSchemata( user? : User ) {
        const retVal : Schema[] = [];

        for ( const schema of schemata ) {
            const itemTypes = metaModel[ schema ].getItemTypes() ?? [];

            for ( const itemType of itemTypes ) {
                if (
                    itemType.id > 0
                    && user?.hasGenericPermissionOnItemType( 'read', itemType.id, schema )
                ) {
                    retVal.push( schema );
                    break;
                }
            }
        }

        retVal.sort();

        return retVal;
    }

    getCreateNewFormDialog() {
        const {
                  uid,
                  user
              }                           = this.props,
              {
                  showCreateNewFormDialog,
                  createNewFormDialogKey,
                  createNewFormDialogKeyError,
                  createNewFormDialogSchema,
                  createNewFormDialogItemType
              }                           = this.state,
              okDisabled                  = Boolean( !createNewFormDialogKey || !createNewFormDialogItemType ),
              selectableSchemata          = this.getSelectableSchemata( user ),
              style : React.CSSProperties = {
                  width            : '33rem',
                  minWidth         : '33rem',
                  [ '--min-width' ]: '33rem',
                  minHeight        : '25vh'
              };

        // only one selectable schemata and not set -> set it
        if ( selectableSchemata.length === 1 && createNewFormDialogSchema !== selectableSchemata[ 0 ] ) {
            this.setState( { createNewFormDialogSchema: selectableSchemata[ 0 ] } );
            return;
        }

        return <Dialog
            key={`${uid}_createNewFormDialog`}
            id={`${uid}_createNewFormDialog`}

            visible={showCreateNewFormDialog}
            onHide={this.onCreateNewFormDialogHide}
            focusOnShow={false}
            closeOnEscape={false}
            className="yn-form-dialog"
            style={style}
            appendTo={( document.querySelector( '.yn-tabpanel .p-tabview-panel' ) ?? undefined ) as HTMLElement | undefined}
            header={__( 'YN.base.frontend.formDesigner.createNewDialog.title' )}
            footer={<div>
                <Button
                    id={`${uid}_okButton`}
                    key="okButton"
                    label={__( 'YN.base.common.ok' )}
                    className="p-button-outlined mr-3"
                    disabled={okDisabled}
                    onClick={this.onCreateNewFormDialogOkButtonClick}
                />
                <Button
                    id={`${uid}_cancelButton`}
                    key="cancelButton"
                    label={__( 'YN.base.common.cancel' )}
                    className="p-button-secondary p-button-outlined"
                    onClick={this.onCreateNewFormDialogHide}
                />
            </div>}
        >
            <StringField
                uid={`${uid}_keyField`}
                label={__( 'YN.base.frontend.formDesigner.createNewDialog.key.label' )}
                value={createNewFormDialogKey}
                errorMessage={createNewFormDialogKeyError}
                style={{ width: '30rem' }}
                labelPosition="top"
                infoTextLayout="hidden"
                markDirty={false}
                onChange={this.onCreateNewFormDialogSetKey}
            />
            <FieldWrapper
                uid={`${uid}_itemTypeField`}
                label={__( 'YN.base.frontend.formDesigner.createNewDialog.itemType.label' )}
                labelPosition="top"
                infoTextLayout="hidden"
                isDirty={false}
                autoComplete={false}
            >
                {selectableSchemata.length > 1 &&
                    <SchemaDropdown
                        uid={uid}
                        value={createNewFormDialogSchema}
                        onChange={this.onCreateNewFormDialogSetSchema}

                        style={{ flex: '0 0 7rem', width: '7rem', paddingRight: '.5rem' }}
                    />
                }
                <ItemTypeDropdown
                    uid={uid}

                    schema={createNewFormDialogSchema}
                    value={createNewFormDialogItemType}
                    rawMode={false}
                    showAdditionalRawNames={false}
                    markDirty={false}
                    onChange={this.onCreateNewFormDialogSetItemType}
                    inputStyle={{ width: selectableSchemata.length > 1 ? '23rem' : '30rem' }}
                />
            </FieldWrapper>
        </Dialog>;
    }

    getHeaderbar( uid : string, formKey : string, formName : string, rootDataObjectItemType : string, rootDataObjectSchema : Schema ) {
        return <Headerbar
            key={`${uid}_headerbar`}

            uid={uid}

            formKey={formKey}
            formName={formName}

            rootDataObjectItemType={rootDataObjectItemType}
            rootDataObjectSchema={rootDataObjectSchema}
        />;
    }

    getToolbar( uid : string, dataContext : string, rootItemId : number, dataRootItemId : number | undefined, dataItemType : string, dataSchema : Schema, rootItem : Item, mode : FormDesignerMode, readOnly : boolean, saveButtonErrorCount : number, saveButtonErrorTooltip : React.ReactNode | React.ReactNode[], newLayoutElementsWidth : string, showHiddenLayoutElements : boolean, hasHiddenLayoutElements : boolean | undefined ) {
        return <Toolbar
            key={`${uid}_ItemFinderToolbar`}

            uid={uid}

            dataContext={dataContext}
            schema="CFG"

            rootItemId={rootItemId}

            dataRootItemId={dataRootItemId}
            dataItemType={dataItemType}
            dataSchema={dataSchema}
            onDataRootItemIdChange={this.onDataRootItemIdChange}

            treeViewItem={rootItem}

            mode={mode}

            saveButtonDisabled={readOnly}
            saveButtonErrorCount={saveButtonErrorCount}
            saveButtonErrorTooltip={saveButtonErrorTooltip}
            deleteButtonDisabled={readOnly}
            newLayoutElementsWidth={newLayoutElementsWidth}
            showHiddenLayoutElements={showHiddenLayoutElements}

            onNewLayoutElementsWidthChange={this.onNewLayoutElementsWidthChange}
            onShowHiddenLayoutElementsChange={this.onShowHiddenLayoutElementsChange}
            onReloadButtonClick={this.onReloadButtonClick}

            showHiddenLayoutElementsCheckbox={mode === 'design' && hasHiddenLayoutElements}
        />;
    }

    getDesignerTree( uid : string, dataContext : string, treeProps : any, parentUid : string, rootItemId : number, readOnly : boolean, additionalElusiveDataContexts : string[], selectedLayoutElementId : number | undefined, newLayoutElementsWidth : string, repeatedDataObjects? : number[] ) {
        return <DesignerTree
            key={uid}

            ref={( el ) => this.tree = el}

            {...treeProps}

            uid={uid}
            dataContext={dataContext}
            parentUid={parentUid}
            rootItemId={rootItemId}

            readOnly={readOnly}

            selectedLayoutElementId={selectedLayoutElementId}
            newLayoutElementsWidth={newLayoutElementsWidth}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            repeatedDataObjects={repeatedDataObjects}

            onSelectLayoutElement={this.onSelectLayoutElement}
            onNewLayoutElementDragStart={this.onNewLayoutElementDragStart}
            onMarkLayoutElementsForAlias={this.onMarkLayoutElementsForAlias}

            className="yn-sidebar-content p-tree"
        />;
    }

    getDesignerTreeEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getFormDesigner( configUid : string, configDataContext : string, uid : string, formDesignerProps : any, mode : FormDesignerMode, showHiddenLayoutElements : boolean, dataContext : string, configRootItemId : number, readOnly : boolean, additionalElusiveDataContexts : string[], rootItemFormState : State['rootItemFormState'], rootItemId? : number, selectedLayoutElementId? : number ) {
        return <FormDesigner
            key={uid}

            ref={( el ) => this.formDesigner = el}

            {...formDesignerProps}

            uid={uid}
            configUid={configUid}

            mode={mode}
            showHiddenLayoutElements={showHiddenLayoutElements}
            readOnly={readOnly}

            configDataContext={configDataContext}
            configRootItemId={configRootItemId}

            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            rootItemId={rootItemId}
            rootItemFormState={rootItemFormState}

            selectedLayoutElementId={selectedLayoutElementId}
            onMarkFieldsForAlias={this.onMarkFieldsForAlias}
            onSelectLayoutElement={this.onSelectLayoutElement}
        />;
    }

    getFormDesignerEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getForm( uid : string, dataContext : string, formProps : any, additionalElusiveDataContexts : string[], viewKey : string, rootItemId : number, readOnly : boolean, treeParentItemId : number, treeRootItemId : number, treeVariables : Variables | undefined ) {
        return <Form
            key={uid}

            {...formProps}

            uid={uid}
            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            schema="CFG"
            viewKey={viewKey}
            mode="embedded"
            readOnly={readOnly}
            rootItemId={rootItemId}
            rootItemKey=""
            rootItemType=""
            treeParentItemId={treeParentItemId}
            treeRootItemId={treeRootItemId}
            treeVariables={treeVariables}
        />
    }

    getFormEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    /**
     * React render method
     * @return {*}
     */
    render() {
        const {
                  dataContext,
                  uid,
                  dataRootItemId,
                  status,
                  mode,
                  timestamp,
                  timezone,
                  menuWidth,
                  menu2Width,
                  subPanelProps,
                  showHiddenLayoutElements,
                  selectedLayoutElementId,
                  newLayoutElementsWidth,
                  elusiveData,
                  user
              }                           = this.props as AllProps,
              {
                  errorCount,
                  errorMessage,
                  errorTooltip,
                  initialized,
                  rootItemFormState,
                  rootItemId = 0,
                  showCreateNewFormDialog
              }                           = this.state,
              rootItem                    = global.data[ dataContext ]?.items?.[ rootItemId ],
              dataStructure               = this.formDesigner?.dataStructure,
              dataObjectItems             = dataStructure
                  ? extractDataObjectsFromDataStructure( dataStructure ).map( dO => global.data[ dataContext ]?.items?.[ dO.origId ] )
                  : [],
              layoutElementItems          = Object.keys( this.formDesigner?.layoutElements ?? {} ).map( layoutElementId => global.data[ dataContext ]?.items?.[ layoutElementId ] ),
              repeatedDataObjects         = this.formDesigner?.repeatedDataObjects,
              rootDataContext             = `${dataContext}_data`,
              readOnly                    = !( user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) || !rootItem?.properties.get( metaModel[ 'CFG' ].getPropertyType( 'SystemRelevant' )?.id ?? 0 )?.value ),
              rawMode                     = false,
              showAdditionalRawNames      = false,
              selectedLayoutElement       = selectedLayoutElementId ? global.data[ dataContext ]?.items[ selectedLayoutElementId ] : undefined,
              currentItemId               = selectedLayoutElement?.id ?? rootItemId,
              currentItem                 = selectedLayoutElement ?? rootItem,
              panelConfigs                = user && currentItem ? user.getPanelPropsForItemType( 'CFG', currentItem.itemTypeId, undefined, 'Form' ) as FormPanelProps[] : undefined,
              panelConfig                 = panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^FdSp_/ ) ) ?? panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^XdSp_/ ) ) ?? panelConfigs?.[ 0 ] ?? null,
              allBelongsToReferenceSetIds = currentItem ? metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( currentItem.itemTypeId, '*', 'belongsTo', 'src' )?.map( rset => rset.id ) : undefined,
              parentPayloadId             = dataUtils.findReferences( dataContext, allBelongsToReferenceSetIds, undefined, currentItemId, true )?.[ 0 ]?.destItemId ?? rootItemId,
              designerTreeUid             = `tree-${rootItemId}`,
              fullDesignerTreeUid         = `${uid}_${designerTreeUid}`,
              formUid                     = `form-${currentItemId}`,
              fullFormUid                 = `${uid}_${formUid}`,
              formDesignerUid             = `formDesigner-${rootItemId}`,
              fullFormDesignerUid         = `${uid}_${formDesignerUid}`,
              attributesUid               = `attributes-${currentItemId}`,
              fullAttributesUid           = `${uid}_${attributesUid}`;

        return <>
            {showCreateNewFormDialog &&
                this.getCreateNewFormDialog()
            }
            {elusiveData?.showCouldNotSavePopup &&
                <Dialog
                    key={`${uid}_dialog`}
                    header={__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.title' )}
                    footer={<div><Button label="OK" className="p-button-outlined"
                                         onClick={() => this.props.setElusiveData( uid, { showCouldNotSavePopup: false } )}/></div>}
                    maskClassName="yn-dialog-panel-modal"
                    appendTo="self"
                    position="top"
                    visible={true}
                    style={{ width: 'auto', maxWidth: '80%' }}
                    closable={false}
                    className="yn-error-dialog"
                    onHide={() => {
                    }}>
                    {errorMessage}
                </Dialog>
            }
            {this.getHeaderbar(
                uid,
                rootItem?.key ?? '',
                rootItem?.name ?? '',
                dataStructure?.item.objectType ?? '',
                ( dataStructure?.item.objectSchema ?? 'DATA' ).toUpperCase() as Schema
            )}
            {this.getToolbar(
                uid,
                dataContext,
                rootItemId,
                dataStructure ? dataRootItemId : undefined,
                dataStructure?.item.objectType ?? '',
                ( dataStructure?.item.objectSchema ?? 'DATA' ).toUpperCase() as Schema,
                rootItem,
                mode,
                readOnly,
                errorCount,
                errorTooltip,
                newLayoutElementsWidth,
                showHiddenLayoutElements,
                this.formDesigner?.hasHiddenLayoutElements
            )}
            <LoadingMaskPanel forComponent={uid} className="yn-flex-layout-parent">
                <Toast
                    ref={( el ) => this.toast = el}

                    key={`${uid}_toast`}

                    position="bottom-right"
                    style={{ position: 'absolute', marginTop: '0' }}
                />
                <ResizeableLeftMenuPanel
                    key={`${uid}_menuContentPanel`}

                    uid={uid}

                    menuWidth={menuWidth}
                    minMenuWidth={15}
                    maxMenuWidth={25}
                    menuClassName={mode === 'input' ? 'hidden' : undefined}
                    gutterSize={mode === 'input' ? 0 : undefined}
                    contentStyle={this.menuContentPanelContentStyle}
                    onMenuScroll={this.onMenuScroll}
                    onMenuWidthChange={this.onMenuWidthChange}

                    pt={mode === 'input'
                        ? this.menuContentPanelsPt
                        : undefined}

                    menu={initialized && !showCreateNewFormDialog
                        ? this.getDesignerTree(
                            fullDesignerTreeUid,
                            dataContext,
                            subPanelProps?.[ designerTreeUid ],
                            uid,
                            rootItemId,
                            readOnly,
                            [ fullFormDesignerUid, uid ],
                            selectedLayoutElementId,
                            newLayoutElementsWidth,
                            repeatedDataObjects
                        )
                        : <></>
                    }
                    content={<div className="yn-flex-layout-parent" style={{ width: '100%' }}>
                        <ResizeableRightMenuPanel
                            key={`${uid}_menu2ContentPanel`}

                            uid={uid}

                            menuWidth={menu2Width}
                            minMenuWidth={15}
                            maxMenuWidth={33}
                            menuClassName={`yn-content-panel-container yn-menu-form yn-formdesigner-properties-panel ${mode === 'input' ? 'hidden' : ''}`}
                            gutterSize={mode === 'input' ? 0 : undefined}
                            contentClassName={( mode === 'input' ) ? '' : undefined}
                            onMenuWidthChange={this.onMenu2WidthChange}

                            pt={mode === 'input'
                                ? this.menuContentPanelsPt
                                : undefined}

                            content={<LoadingMaskPanel forComponent={fullFormDesignerUid}
                                                       className="yn-flex-layout-parent yn-content-panel-container"
                                                       style={{ position: 'absolute', width: '100% !important', height: '100% !important', padding: 'calc(.5rem - 1px) calc(1.5rem - 1px) !important' }}>
                                {initialized && !showCreateNewFormDialog
                                    ? this.getFormDesigner(
                                        uid,
                                        dataContext,
                                        fullFormDesignerUid,
                                        subPanelProps?.[ formDesignerUid ],
                                        mode,
                                        showHiddenLayoutElements,
                                        rootDataContext,
                                        rootItemId,
                                        readOnly,
                                        [ uid + '_DataObjects', fullDesignerTreeUid, uid ],
                                        rootItemFormState,
                                        dataRootItemId,
                                        selectedLayoutElementId ?? currentItemId
                                    )
                                    : undefined
                                }
                            </LoadingMaskPanel>}
                            menu={initialized && !showCreateNewFormDialog
                                ? <LoadingMaskPanel key={`${uid}_formview_loadingmask`}
                                                    forComponent={panelConfig?.panelType === 'Form' ? fullFormUid : fullAttributesUid}
                                                    className="yn-flex-layout-parent">
                                    <div>
                                        {panelConfig?.panelType === 'Form' && currentItemId
                                            ? this.getForm(
                                                fullFormUid,
                                                dataContext,
                                                subPanelProps?.[ formUid ],
                                                [ uid + '_DataObjects', fullDesignerTreeUid, fullFormDesignerUid, uid ],
                                                panelConfig.viewKey,
                                                currentItemId,
                                                readOnly,
                                                parentPayloadId,
                                                rootItemId,
                                                {
                                                    dataStructure,
                                                    dataObjectItems,
                                                    layoutElementItems,
                                                    inheritedProps: this.formDesigner?.layoutElementsInheritedProps?.[ currentItemId ],
                                                    fieldConfig   : this.formDesigner?.layoutElementsProps?.[ currentItemId ]
                                                }
                                            )
                                            : undefined
                                        }
                                        {panelConfig?.panelType !== 'Form' && currentItem &&
                                            <div key={`${uid}_formview`}>
                                                <AttributesForm
                                                    key={fullAttributesUid}

                                                    {...subPanelProps?.[ attributesUid ]}

                                                    uid={fullAttributesUid}
                                                    dataContext={dataContext}
                                                    style={{ padding: 0 }}
                                                    schema="CFG"
                                                    status={status}
                                                    timestamp={timestamp}
                                                    timezone={timezone}
                                                    item={currentItem}
                                                    rawMode={rawMode}
                                                    showAdditionalRawNames={showAdditionalRawNames}
                                                    editable
                                                />
                                            </div>
                                        }
                                    </div>
                                </LoadingMaskPanel>
                                : <></>
                            }
                        />
                    </div>}
                />
            </LoadingMaskPanel>
        </>;
    }
}


// redux stuff

/**
 *
 * @param state
 * @param ownProps
 */
function mapStateToProps( state : StoreState, ownProps : AllProps ) {
    const retVal : Partial<Props> = {
        allElusiveData  : undefined,
        elusiveData     : undefined,
        intercomMessages: undefined,
        user            : undefined
    };

    retVal.allElusiveData = state.elusiveData;

    if ( ownProps.uid ) {

        // get elusiveData
        if ( state.elusiveData?.[ ownProps.uid ] ) {
            retVal.elusiveData = state.elusiveData[ ownProps.uid ];
        }

        // get intercom messages
        if ( state.intercom?.[ ownProps.uid ] ) {
            retVal.intercomMessages = state.intercom[ ownProps.uid ];
        }

        // get the running xhr request belonging to this component (uid)
        if ( state.xhr?.[ ownProps.uid ] ) {
            retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
        }
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * Bind action creators to component props
 * @param dispatch
 * @return {*}
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect( mapStateToProps, mapDispatchToProps )( FormDesignerView );
